<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { AccountClient, ApiError, dateTime } from '../api';
import Icon from './Icon.vue';
import HaConnectorPanel from './HaConnectorPanel.vue';
import ConnectorHistory from './ConnectorHistory.vue';
import ChatgptConnectorPanel from './ChatgptConnectorPanel.vue';

type Recent = { at: number; source: string; level: string; success: boolean; detail: string };
type Settings = { enabled: boolean; url: string; recent: Recent[]; mcpUrl?: string };
const props = defineProps<{ client: AccountClient }>();
const emit = defineEmits<{ notice: [message: string, kind?: 'success' | 'error' | 'loading' | 'info']; reauth: [retry: () => Promise<void>] }>();
const endpoint = '/api/admin/connectors/home-status';
const saved = ref<Settings | null>(null), enabled = ref(false), url = ref('');
const loading = ref(false), busy = ref(''), error = ref(''), mcpUrl = ref('');
const dirty = computed(() => saved.value && (enabled.value !== saved.value.enabled || url.value !== saved.value.url));
const status = computed(() => !saved.value?.url ? '未配置' : !saved.value.enabled ? '已停用' : !saved.value.recent.length ? '已配置 · 尚未测试' : saved.value.recent[0]?.success ? '最近推送成功' : '最近推送失败');
async function load() {
  loading.value = true; error.value = '';
  try {
    const data = await props.client.request<Settings>(endpoint);
    saved.value = data; enabled.value = data.enabled; url.value = data.url; mcpUrl.value = data.mcpUrl || '';
  } catch (err: any) { error.value = err.message; }
  finally { loading.value = false; }
}
async function save(snapshot = { enabled: enabled.value, url: url.value }) {
  if (busy.value) return;
  busy.value = 'save'; error.value = '';
  try { saved.value = await props.client.request<Settings>(endpoint, 'PUT', snapshot); enabled.value = saved.value.enabled; url.value = saved.value.url; emit('notice', '连接器设置已保存', 'success'); }
  catch (err: any) { if (err instanceof ApiError && err.status === 428) emit('reauth', () => save(snapshot)); else error.value = err.message; }
  finally { busy.value = ''; }
}
async function test() {
  if (busy.value || dirty.value) return;
  busy.value = 'test'; error.value = ''; emit('notice', '正在向家庭服务器发送测试状态', 'loading');
  try { await props.client.request(`${endpoint}/test`, 'POST', {}); emit('notice', '家庭服务器已接收测试状态', 'success'); }
  catch (err: any) { error.value = err.message; emit('notice', err.message, 'error'); }
  finally {
    try { saved.value = await props.client.request<Settings>(endpoint); } catch { error.value ||= '最近调用记录刷新失败，请稍后刷新'; }
    busy.value = '';
  }
}
onMounted(load);
</script>

<template>
  <div>
    <header class="page-heading"><span class="eyebrow">平台管理</span><h1>连接器<span class="accent-dot">.</span></h1><p>连接家中的墨水屏、Home Assistant 与 ChatGPT。</p></header>
    <p v-if="error" class="inline-error" role="alert">{{ error }}</p>
    <div v-if="loading && !saved" class="empty-block"><Icon name="LoaderCircle" class="spin" /><p>正在读取连接器</p></div>
    <button v-else-if="!saved" class="secondary" @click="load">重新加载</button>
    <template v-else>
      <section class="settings-group">
        <div class="settings-row"><span class="icon-tile"><Icon name="House" /></span><div class="grow"><strong>fuqiHome Agent Status</strong><p>将一条状态消息推送到家庭墨水屏。</p></div><span class="tag" role="status">{{ status }}</span></div>
        <form class="connector-form" @submit.prevent="save()">
          <fieldset :disabled="Boolean(busy)">
            <label class="checkbox-label"><input v-model="enabled" type="checkbox" /><span>启用连接器</span></label>
            <label><span>家庭服务器 Webhook 地址</span><input v-model="url" type="url" maxlength="2048" placeholder="http://100.x.x.x:端口/路径" autocomplete="off" :required="enabled" /></label>
            <p class="footnote">使用家庭服务器的 Tailscale IPv4 或 IPv6 地址。运行 fuqiHost 的服务器需要能访问该节点。</p>
            <p class="footnote">地址加密保存，仅用于后端推送，不提供给 MCP Agent。家庭 Webhook 无需密钥。</p>
            <div class="connector-actions"><button class="primary" :disabled="!dirty"><Icon v-if="busy === 'save'" name="LoaderCircle" class="spin" />{{ busy === 'save' ? '正在保存' : '保存设置' }}</button><button type="button" class="secondary" :disabled="!saved.enabled || Boolean(dirty)" @click="test"><Icon v-if="busy === 'test'" name="LoaderCircle" class="spin" />{{ busy === 'test' ? '正在测试' : '发送测试状态' }}</button></div>
            <p class="footnote">测试会实际推送一条有效期 60 秒的消息。修改设置后请先保存。</p>
          </fieldset>
        </form>
      </section>
      <section class="settings-group connector-info"><h2>MCP 接入</h2><p>在 MCP 客户端添加以下地址，使用现有 fuqiHost OAuth 授权。</p><code>{{ mcpUrl }}</code><p>工具 <code>push_agent_status</code> 接收消息正文、可选标题、级别和有效期（秒，默认 300）。</p></section>
      <ConnectorHistory title="最近调用" :count="saved.recent.length" :latest="saved.recent[0]?.detail">
      <template #actions><button class="text-button" :disabled="Boolean(busy) || Boolean(dirty)" @click="load">刷新</button></template>
      <section class="settings-group"><p v-if="!saved.recent.length" class="empty-block">还没有调用记录，保存后可以发送一条测试状态。</p><div v-for="(item, index) in saved.recent" :key="`${item.at}-${index}`" class="settings-row"><Icon :name="item.success ? 'CircleCheck' : 'CircleX'" :class="{ 'danger-text': !item.success }" /><div class="grow"><strong>{{ item.detail }}</strong><p>{{ dateTime(item.at) }} · {{ item.source === 'test' ? '连接测试' : 'MCP Agent' }} · {{ item.level }}</p></div></div></section>
      <p class="footnote">保留最近 20 次推送结果，不记录消息正文。请求成功代表 Webhook 已接收，屏幕是否刷新请以设备为准。</p>
      </ConnectorHistory>
    </template>
    <HaConnectorPanel :client="client" @notice="(message, kind) => emit('notice', message, kind)" @reauth="retry => emit('reauth', retry)" />
    <ChatgptConnectorPanel :client="client" @notice="(message, kind) => emit('notice', message, kind)" @reauth="retry => emit('reauth', retry)" />
  </div>
</template>

<style scoped>
.connector-form, .connector-info { padding: 24px; }
fieldset { padding: 0; border: 0; min-width: 0; }
.connector-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 20px; }
.connector-info { margin-top: 24px; margin-bottom: 28px; }
.connector-info code { overflow-wrap: anywhere; }
.connector-info p { color: var(--muted); line-height: 1.7; }
@media (max-width: 600px) { .connector-form, .connector-info { padding: 18px; } .settings-row { flex-wrap: wrap; } }
</style>
